import React from 'react';
import { Link } from 'react-router-dom';
import Icon from './DashboardIcons';
import { getVisibleNavigation } from '../../config/organizationNavigation';

const formatNumber = (n) => new Intl.NumberFormat('ar-EG').format(n);
const formatMoney = (n) => new Intl.NumberFormat('ar-SA', { style: 'currency', currency: 'SAR', maximumFractionDigits: 0 }).format(n);

const StatCard = ({ icon, title, value, sub, testId, href }) => {
  const subLines = Array.isArray(sub) ? sub : sub ? [sub] : [];
  const content = (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="min-w-0 truncate text-sm font-medium text-slate-500">{title}</span>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-[#16233b]">
          <Icon name={icon} className="w-[18px] h-[18px]" />
        </span>
      </div>
      <div className="mt-2 break-words text-2xl font-bold leading-snug text-slate-900" dir="rtl">{value}</div>
      <div className="mt-1 min-h-[1rem] space-y-0.5" dir="rtl">
        {subLines.length > 0
          ? subLines.map((line, i) => <div key={i} className="break-words text-xs leading-snug text-slate-500">{line}</div>)
          : <div className="text-xs leading-snug text-slate-500">{'\u00A0'}</div>}
      </div>
    </>
  );
  const className = 'flex h-full min-h-[112px] flex-col justify-between overflow-hidden rounded-xl border border-slate-200 bg-white p-4 transition-colors';
  return href ? (
    <Link to={href} className={`${className} hover:border-teal-300 hover:bg-teal-50/40`} data-testid={testId}>{content}</Link>
  ) : (
    <div className={className} data-testid={testId}>{content}</div>
  );
};

export const DashboardStats = ({ stats, financial, showFinance, user }) => {
  if (!stats) return null;
  const cards = [];

  if (typeof stats.total_players === 'number') {
    cards.push({ icon: 'users', title: 'اللاعبون النشطون', value: formatNumber(stats.total_players), testId: 'stat-players', href: '/players' });
  }
  if (typeof stats.total_teams === 'number') {
    cards.push({
      icon: 'userGroup',
      title: 'الفرق',
      value: formatNumber(stats.total_teams),
      sub: typeof stats.total_categories === 'number' ? `${formatNumber(stats.total_categories)} فئة سنية` : null,
      testId: 'stat-teams',
      href: '/teams',
    });
  }
  if (typeof stats.total_matches === 'number') {
    cards.push({
      icon: 'calendar',
      title: stats.period_matches_label ? `مباريات ${stats.period_matches_label}` : 'إجمالي المباريات',
      value: formatNumber(stats.total_matches),
      sub: stats.upcoming_matches > 0 ? `${formatNumber(stats.upcoming_matches)} مباراة قادمة خلال أسبوعين` : null,
      testId: 'stat-matches',
      href: '/matches',
    });
  }
  if (showFinance && financial && typeof financial.today_income === 'number') {
    cards.push({
      icon: 'banknotes',
      title: 'دخل اليوم',
      value: formatMoney(financial.today_income),
      sub: `دخل الشهر: ${formatMoney(financial.month_income || 0)}`,
      testId: 'stat-today-income',
      href: '/accounts?period=month',
    });
  }
  if (showFinance && financial && typeof financial.net_balance === 'number') {
    cards.push({
      icon: 'banknotes',
      title: financial.period_label ? `الصافي المالي (${financial.period_label})` : 'الصافي المالي',
      value: formatMoney(financial.net_balance),
      sub: [`إيرادات ${formatMoney(financial.total_revenues)}`, `مصروفات ${formatMoney(financial.total_expenses)}`],
      testId: 'stat-finance',
      href: '/accounts',
    });
  }
  const canViewEmployees = getVisibleNavigation(user).some((g) => g.items.some((i) => i.href === '/staff-admin'));
  if (canViewEmployees && typeof stats.active_employees === 'number') {
    cards.push({ icon: 'briefcase', title: 'الموظفون النشطون', value: formatNumber(stats.active_employees), testId: 'stat-employees', href: '/staff-admin' });
  }

  if (cards.length === 0) return null;

  return (
    <div className="grid grid-cols-1 gap-4 min-[380px]:grid-cols-2 md:grid-cols-3 xl:grid-cols-6" data-testid="dashboard-stats">
      {cards.map((c) => (
        <StatCard key={c.testId} {...c} />
      ))}
    </div>
  );
};

export default DashboardStats;
